/**
 * Password page components.
 *
 * @element password-main   – wraps the password-page sections.
 * @element password-header – toggles the transparent-header style.
 * @element password-footer – toggles the transparent-footer style.
 * @element password-modal  – login modal; auto-opens on form errors.
 */

/**
 * Password Main
 * Adds a `with-section` class when a single extra section is present.
 *
 * @element password-main (is="main")
 */
if (!customElements.get('password-main')) {
  customElements.define(
    'password-main',
    class PasswordMain extends HTMLElement {
      connectedCallback() {
        if (this.querySelectorAll('.shopify-section').length === 1) {
          this.classList.add('with-section');
        }
      }
    },
    { extends: 'main' }
  );
}

/**
 * Password Header
 * Applies a transparent-header class when the first section below requests it.
 *
 * @element password-header (is="header")
 */
if (!customElements.get('password-header')) {
  customElements.define(
    'password-header',
    class PasswordHeader extends HTMLElement {
      get #allowTransparent() {
        return Boolean(document.querySelector('.shopify-section:last-child [allow-transparent-header]'));
      }

      get #headerSection() {
        return document.querySelector('.password-header-section');
      }

      connectedCallback() {
        this.#update();

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:load',    this.#onSectionChange);
          document.addEventListener('shopify:section:unload',  this.#onSectionChange);
          document.addEventListener('shopify:section:reorder', this.#onSectionChange);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load',    this.#onSectionChange);
        document.removeEventListener('shopify:section:unload',  this.#onSectionChange);
        document.removeEventListener('shopify:section:reorder', this.#onSectionChange);
      }

      #onSectionChange = () => this.#update();

      #update() {
        const transparent = this.#allowTransparent;
        document.documentElement.classList.toggle('password-header-transparent', transparent);

        if (transparent && this.#headerSection) {
          this.#headerSection.classList.add('no-animate');
          setTimeout(() => this.#headerSection?.classList.remove('no-animate'), 500);
        }
      }
    },
    { extends: 'header' }
  );
}

/**
 * Password Footer
 * Applies a transparent-footer class when the last section requests it.
 *
 * @element password-footer (is="footer")
 */
if (!customElements.get('password-footer')) {
  customElements.define(
    'password-footer',
    class PasswordFooter extends HTMLElement {
      get #allowTransparent() {
        return Boolean(document.querySelector('.shopify-section:last-child [allow-transparent-footer]'));
      }

      get #footerSection() {
        return document.querySelector('.password-footer-section');
      }

      connectedCallback() {
        this.#update();

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:load',    this.#onSectionChange);
          document.addEventListener('shopify:section:unload',  this.#onSectionChange);
          document.addEventListener('shopify:section:reorder', this.#onSectionChange);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load',    this.#onSectionChange);
        document.removeEventListener('shopify:section:unload',  this.#onSectionChange);
        document.removeEventListener('shopify:section:reorder', this.#onSectionChange);
      }

      #onSectionChange = () => this.#update();

      #update() {
        const transparent = this.#allowTransparent;
        document.documentElement.classList.toggle('password-footer-transparent', transparent);

        if (transparent && this.#footerSection) {
          this.#footerSection.classList.add('no-animate');
          setTimeout(() => this.#footerSection?.classList.remove('no-animate'), 500);
        }
      }
    },
    { extends: 'footer' }
  );
}

/**
 * Password Modal
 * Login modal for the password page. Auto-opens if a form input is invalid.
 *
 * @element password-modal
 * @extends ModalElement
 */
if (!customElements.get('password-modal')) {
  customElements.define(
    'password-modal',
    class PasswordModal extends ModalElement {
      get shouldLock() { return true; }

      connectedCallback() {
        super.connectedCallback();

        if (this.querySelector('input[aria-invalid="true"]')) {
          this.show();
        }
      }
    }
  );
}
